import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, Flame, ExternalLink } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';

export default function StrugglingItemActivitiesDialog({ 
  open, 
  onClose, 
  item, 
  progressReports, 
  activities,
  officers,
  isRedLine 
}) {
  // Find all reports that flagged this item
  const reportsWithItem = progressReports.filter(report => 
    report.flagged_items?.includes(item)
  );

  // Get unique activities
  const activityIds = [...new Set(reportsWithItem.map(r => r.activity_id))];
  const relatedActivities = activities.filter(a => activityIds.includes(a.id));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="dash-strugglingitemactivitiesdialog-dialog" className="bg-slate-900 text-white border-slate-800 max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            {isRedLine ? (
              <Flame className="w-6 h-6 text-rose-400" />
            ) : (
              <Shield className="w-6 h-6 text-indigo-400" />
            )}
            Activities Struggling With: {item}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          {relatedActivities.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              No activities found
            </div>
          ) : (
            relatedActivities.map(activity => {
              const activityReports = reportsWithItem.filter(r => r.activity_id === activity.id);
              const latestReport = activityReports.sort((a, b) => 
                new Date(b.reported_at) - new Date(a.reported_at)
              )[0];
              const reporter = officers.find(o => o.user_email === latestReport?.reported_by);

              return (
                <div 
                  key={activity.id} 
                  className="p-4 bg-slate-950/50 rounded-lg border border-slate-800 hover:border-indigo-500 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">
                          {activity.activity_code}
                        </Badge>
                        <Badge className={`text-xs ${
                          activity.status === 'active' ? 'bg-emerald-500/20 text-emerald-400 border-none' :
                          activity.status === 'pending_review' ? 'bg-amber-500/20 text-amber-400 border-none' :
                          'bg-slate-500/20 text-slate-400 border-none'
                        }`}>
                          {activity.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <h4 className="text-slate-200 font-medium mb-1">{activity.title}</h4>
                      <div className="text-xs text-slate-400 space-y-1">
                        <div>
                          Reported by: {reporter?.callsign || latestReport?.reported_by}
                        </div>
                        <div>
                          Last flagged: {latestReport && format(new Date(latestReport.reported_at), 'MMM d, yyyy HH:mm')}
                        </div>
                        <div>
                          Times flagged: {activityReports.length}
                        </div>
                      </div>
                    </div>
                    <Link to={createPageUrl('activities')}>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}